import { IFormsTemplate } from '@commenda/api';
import { Badge, Button, useToggle } from '@commenda/kakashi';
import { ChevronDown, ChevronRight } from 'lucide-react';
import DraggableSavedModule from './DraggableSavedModule';

interface SavedModulesSectionProps {
  modules: IFormsTemplate[];
  isLoading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  defaultExpanded?: boolean;
}

export default function SavedModulesSection({
  modules,
  isLoading,
  hasMore = false,
  onLoadMore,
  defaultExpanded = true,
}: SavedModulesSectionProps) {
  const isExpanded = useToggle(defaultExpanded);

  if (modules.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-col h-screen pb-48">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          isExpanded.toggle();
        }}
        className="flex items-center justify-between w-full px-2 py-2 hover:bg-bg-secondary rounded-lg"
      >
        <div className="flex items-center gap-2 h-full">
          <span className="txt-small-medium text-text-secondary">
            Saved Modules
          </span>
          <Badge size="sm" variant="blue">
            {modules.length}
          </Badge>
        </div>
        {isExpanded.value ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-tertiary" />
        )}
      </button>
      {isExpanded.value && (
        <div className="mt-2 space-y-2 overflow-y-scroll h-full flex flex-col">
          {isLoading ? (
            <div className="text-center py-4">
              <p className="txt-xs-regular text-text-tertiary">
                Loading modules...
              </p>
            </div>
          ) : (
            modules.map((module) => (
              <DraggableSavedModule key={module.id} module={module} />
            ))
          )}
          {hasMore && (
            <div className="flex justify-center py-4">
              <Button
                size="sm"
                variant="tertiary"
                className="w-full text-text-tertiary text-sm"
                onClick={onLoadMore}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
